import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { setBearer, formatApiErrorDetail } from "@/lib/api";
import { ensureRealtime, disconnectRealtime } from "@/hooks/useRealtime";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous
  const [ready, setReady] = useState(false);

  const bootstrap = useCallback(async () => {
    // Skip auth probe on purely public kiosk/display pages
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (path.startsWith("/borne") || path.startsWith("/ecran")) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 is expected when unauthenticated; log only unexpected statuses
      if (err?.response && err.response.status !== 401) {
        console.warn("[Auth] bootstrap /auth/me failed", err.response.status, err.response.data);
      }
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = useCallback(async (accessCode) => {
    try {
      const { data } = await api.post("/auth/login", { access_code: accessCode });
      if (data.access_token) {
        setBearer(data.access_token);
        ensureRealtime(data.access_token);
      }
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Server may already have expired the session — logout locally regardless
      console.debug("[Auth] logout API call failed (proceeding locally)", err?.message || err);
    }
    setBearer(null);
    disconnectRealtime();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, ready, login, logout, refreshUser: bootstrap }),
    [user, ready, login, logout, bootstrap],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
